import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TimeLogApi } from '../../../api';
import { useContainer } from '../../useContainer';
import { UserTimeLogRateBreakdownDto } from '../../../commonTypes/timelog.types';
import { setCachedUserTimeLogs, selectCachedUserTimeLogs } from '../../../store/slices/projects.slice';

interface UseHttpGetUserTimeLogsParams {
    month: string;
    year: number;
    projectId: string;
}

interface UserTimeLogsResult {
    totalSubmitted: number;
    totalUnsubmitted: number;
    currentMonthHours: number;
    totalLoggedHours: number;
    rateBreakdown?: UserTimeLogRateBreakdownDto[];
    isReady: boolean;
}

const useHttpGetUserTimeLogs = (params: UseHttpGetUserTimeLogsParams): UserTimeLogsResult => {
    const timeLogApi = useContainer(TimeLogApi);
    const dispatch = useDispatch();
    const cacheKey = `${params.projectId}-${params.month}-${params.year}`;
    const cachedData = useSelector(selectCachedUserTimeLogs(cacheKey));
    const [isReady, setIsReady] = useState(false);
    const [totalSubmitted, setTotalSubmitted] = useState(0);
    const [totalUnsubmitted, setTotalUnsubmitted] = useState(0);
    const [currentMonthHours, setCurrentMonthHours] = useState(0);
    const [totalLoggedHours, setTotalLoggedHours] = useState(0);
    const [rateBreakdown, setRateBreakdown] = useState<UserTimeLogRateBreakdownDto[] | undefined>();

    useEffect(() => {
        if (!params.month || !params.year || !params.projectId) {
            setRateBreakdown(undefined);
            setIsReady(true);
            return;
        }

        if (cachedData) {
            setTotalSubmitted(cachedData.totalSubmitted);
            setTotalUnsubmitted(cachedData.totalUnsubmitted);
            setCurrentMonthHours(cachedData.currentMonthHours);
            setTotalLoggedHours(cachedData.totalLoggedHours);
            setRateBreakdown(cachedData.rateBreakdown);
            setIsReady(true);
            return;
        }

        setIsReady(false);
        const { exec, abort } = timeLogApi.getUserTimeLogs({
            query: {
                month: params.month,
                year: params.year,
                projectId: params.projectId,
            },
            onSuccess: res => {
                const data = {
                    totalSubmitted: res.body.totalSubmitted,
                    totalUnsubmitted: res.body.totalUnsubmitted,
                    currentMonthHours: res.body.currentMonthHours,
                    totalLoggedHours: res.body.totalLoggedHours,
                    rateBreakdown: res.body.rateBreakdown,
                };
                dispatch(setCachedUserTimeLogs({ key: cacheKey, data }));
                setTotalSubmitted(data.totalSubmitted);
                setTotalUnsubmitted(data.totalUnsubmitted);
                setCurrentMonthHours(data.currentMonthHours);
                setTotalLoggedHours(data.totalLoggedHours);
                setRateBreakdown(data.rateBreakdown);
            },
            onError: error => {
                console.error('Error fetching user time logs:', error);
            },
            onFinally: () => {
                setIsReady(true);
            },
        });

        exec();
        return abort;
    }, [params.month, params.year, params.projectId, cachedData, cacheKey, dispatch]);

    return { totalSubmitted, totalUnsubmitted, currentMonthHours, totalLoggedHours, rateBreakdown, isReady };
};

export default useHttpGetUserTimeLogs;
